import { type FC, useMemo } from "react";

import { Skeleton, Text } from "@mantine/core";

import { type PayoutCountryBreakdown } from "../../../../gql-generated/types";
import { mapCountryFullName } from "../../../mapping/mapCountryFullName";
import { HorizontalBarChartItem } from "../../charts/horizontal-bar-chart/HorizontalBarChartItem";
import { NoDataAvailable } from "../messages.generated";

import { CHART_HEIGHT } from "./constants";

const MAX_COUNTRIES = 5;

interface CountryRow {
    country: string;
    pct: string;
    width: number;
    value: number;
}

const buildRows = (breakdown: readonly PayoutCountryBreakdown[]): CountryRow[] => {
    const totalAmount = breakdown.reduce((sum, entry) => sum + entry.amountMilliUsd, 0);

    if (totalAmount === 0) return [];

    const sorted = [...breakdown].sort((a, b) => b.amountMilliUsd - a.amountMilliUsd);
    const top = sorted.slice(0, MAX_COUNTRIES);
    const rest = sorted.slice(MAX_COUNTRIES);

    const rows: CountryRow[] = top.map((entry) => ({
        country: mapCountryFullName(`COUNTRY_${entry.countryCode}`),
        pct: String(Math.round((entry.amountMilliUsd / totalAmount) * 100)),
        value: entry.amountMilliUsd,
        width: (entry.amountMilliUsd / totalAmount) * 100,
    }));

    if (rest.length > 0) {
        const otherAmount = rest.reduce((sum, entry) => sum + entry.amountMilliUsd, 0);
        rows.push({
            country: "Other",
            pct: String(Math.round((otherAmount / totalAmount) * 100)),
            value: otherAmount,
            width: (otherAmount / totalAmount) * 100,
        });
    }

    return rows;
};

interface CountryBreakdownChartProps {
    countryBreakdown: readonly PayoutCountryBreakdown[];
    loading?: boolean;
}

export const CountryBreakdownChart: FC<CountryBreakdownChartProps> = ({ countryBreakdown, loading }) => {
    const rows = useMemo(() => buildRows(countryBreakdown), [countryBreakdown]);

    if (loading) {
        return <Skeleton h={CHART_HEIGHT} w="100%" />;
    }

    if (rows.length === 0)
        return (
            <Text className="paragraph-p200" c="dimmed" m="auto">
                <NoDataAvailable />
            </Text>
        );

    return (
        <>
            {rows.map((row) => (
                <HorizontalBarChartItem
                    key={row.country}
                    country={row.country}
                    pct={row.pct}
                    value={row.value}
                    width={row.width}
                    valueClassName="paragraph-p200"
                />
            ))}
        </>
    );
};
